import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ResourceBookingSchema, PlanSchema, ResourceSchema, TimeSlot, FeeSchema, DiscountSchema, ResourcePlanSchema, OrderItemSchema, TeamMemberSchema, SearchFilterOptionType } from '../../data/types';
import CancelIcon from '@mui/icons-material/Cancel';
import { 
  capitalizeWords, getBookingStatusColor, 
  areDatesInSameMonth, 
  areBookingsLessThan12Hours, findBookingsForDay, calculateAvailableTimeBlocksForSlots,
  normalizeTeamMembers,
  shouldApplyMemberInventoryLimits,
  getInventoryLimitsForBookingPricing,
  calculateInventoryCostLinesWithLimits,
} from '../../utils/helpers';
import moment from 'moment-timezone';
import { useGetPaymentMethods } from '../../hooks/paymenthooks';
import { useCompleteSignIn, useLoadResourceBookings, useLoadMember } from '../../hooks/authhooks';
import { PaymentMethod } from '@stripe/stripe-js';
import { PaymentsView } from './RequestKitchen';
// import TimesView from './AvailableTimes';
import ReservationCalendar from './ReservationCalendar';
import { format, addMonths } from 'date-fns';
import { styleThousandCommas, getChangedAttributes, extractPlanFields,
  // extractMemberResourceMapFromJsonString, 
  // isFirstBookingWithPlan
} from '../../utils/helpers';
import { ReactComponent as RightArrow } from "../../icons/ScrollRightArrow.svg";
import { ReactComponent as LeftArrow } from "../../icons/ScrollLeftArrow.svg";
import { ReactComponent as CalendarIcon} from "../../icons/CalendarIcon.svg"

import { useLoadCurrentResource } from '../../hooks/apphooks'
import UserAvatar from '../../components/Desktop/UserAvatar';
import AddTeamMembersView, { getResourceCapacityLimits, getTeamMemberDisplayName } from '../../components/AddTeamMembersView';
import ApplicantDocumentationChecklist from '../../components/documentation/ApplicantDocumentationChecklist';
import { isFinancialIntelligenceResource } from '../../utils/fundingPartners';

const mergeTeamMemberPools = (
  memberTeamMembers: TeamMemberSchema[],
  bookingTeamMembers: TeamMemberSchema[]
): TeamMemberSchema[] => {
  const merged = new Map<string, TeamMemberSchema>();
  normalizeTeamMembers([...memberTeamMembers, ...bookingTeamMembers]).forEach((member) => {
    merged.set(member.email.trim().toLowerCase(), member);
  });
  return Array.from(merged.values());
};

const EMPTY_RESOURCE_PLANS: ResourcePlanSchema[] = [];

interface BookingDetailsFormProps {
  booking: ResourceBookingSchema | null;
  isOpen: boolean;
  isEditMode: boolean;
  onClose: () => void;
  onSave: (communityId: string, bookingId: string, changedAttributes: ResourceBookingSchema) => void;
  userBookings?: ResourceBookingSchema[];
  onCancel: (communityId: string, resourceId: string, bookingId: string) => void;
  searchFilterOptions?: SearchFilterOptionType;
  // plans?: PlanSchema[];
}


// const defaultResourceOptions = {
//   name: "Kitchen",
//   type: "kitchen" as const,
//   sun: { open_time: "08:00 AM", close_time: "08:00 PM" },
//   mon: { open_time: "08:00 AM", close_time: "08:00 PM" },
//   tue: { open_time: "08:00 AM", close_time: "08:00 PM" },
//   wed: { open_time: "08:00 AM", close_time: "08:00 PM" },
//   thu: { open_time: "08:00 AM", close_time: "08:00 PM" },
//   fri: { open_time: "08:00 AM", close_time: "08:00 PM" },
//   sat: { open_time: "08:00 AM", close_time: "08:00 PM" },
// };

const BookingDetailsForm: React.FC<BookingDetailsFormProps> = ({
  booking,
  userBookings,
  // isOpen,
  isEditMode,
  onClose,
  onSave,
  onCancel,
  searchFilterOptions,
  // plans = []
}) => {
  const [form, setForm] = useState<ResourceBookingSchema>(() => {
    if (booking) {
      return {
        ...booking,
        start_time: booking.start_time,
        end_time: booking.end_time
      };
    }
    return {} as ResourceBookingSchema;
  });
  const [selectedDay, setSelectedDay] = useState<Date>(
    form?.start_time ? new Date(form.start_time) : new Date()
  );
  const [availableTimeBlocks, setAvailableTimeBlocks] = useState<any[]>();
  // const [selectedBlock, setSelectedBlock] = useState<any>();
  const [currentCalendarDate, setCurrentDate] = useState<Date>(new Date());
  const [nHours, setNHours] = useState<number>(form?.total_hours ? form.total_hours : 0);
  const [isAddingPayMethod, setIsAddingPayMethod] = useState(false);
  const [isEditPay, setIsEditPay] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [availableTeamMembers, setAvailableTeamMembers] = useState<TeamMemberSchema[]>([]);
  const [selectedTeamMemberIds, setSelectedTeamMemberIds] = useState<string[]>([]);
  // Helper function above component
  // const extractPlanFields = (member: any): PlanSchema => {
  //   if (!member) return {};
    
  //   return Object.entries(member)
  //     .filter(([key]) => key in (member as PlanSchema))
  //     .reduce((acc, [key, value]) => ({
  //       ...acc,
  //       [key]: value
  //     }), {});
  // };


  const { currentResource, updateResourceId } = useLoadCurrentResource(booking?.resource_id ? booking.resource_id : "")  
  // const { currentOptions} = useLoadResourceOptions(form?.resource_id ? form.resource_id : "")
  const plans = currentResource?.plans ?? EMPTY_RESOURCE_PLANS;

  // console.log("currentResource", currentResource)
  // console.log("plans", plans)
  // console.log("booking", booking)
  // console.log("form", form)

  const [plan, setPlan] = useState<ResourcePlanSchema>(extractPlanFields(booking as ResourceBookingSchema) as ResourcePlanSchema);
  // const [isChangePlan, setIsChangePlan] = useState(false);

  const { user } = useCompleteSignIn();
  const loggedinUid = (user && user?.uid) ? user.uid : '';
  const { currentMember } = useLoadMember(booking?.community_id || '');

  var { paymentMethods } = useGetPaymentMethods(loggedinUid);
  paymentMethods = (paymentMethods && Array.isArray(paymentMethods)) ? paymentMethods : [];

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    (paymentMethods && paymentMethods.length > 0) ? paymentMethods.find((p: PaymentMethod) => p.id === form?.payment_method) || paymentMethods[0] : {} as PaymentMethod
  );
  const initialPaymentMethodSet = useRef(false);

  const setBookingTeamMemberIds = useCallback((updater: React.SetStateAction<string[]>) => {
    setSelectedTeamMemberIds(updater);
  }, []);

  useEffect(() => {
    if (!isEditMode) return;
    const selected = availableTeamMembers.filter((member) =>
      selectedTeamMemberIds.includes(member.id)
    );
    setForm((prev) => (prev ? { ...prev, team_members: selected } : prev));
  }, [selectedTeamMemberIds, availableTeamMembers, isEditMode]);

  const handleInviteTeamMember = useCallback((member: Omit<TeamMemberSchema, 'id'>) => {
    const normalizedEmail = member.email.trim().toLowerCase();
    if (
      availableTeamMembers.some(
        (existing) => existing.email.trim().toLowerCase() === normalizedEmail
      )
    ) {
      return;
    }
    const { teamMemberMaxCapacity } = getResourceCapacityLimits(currentResource?.resource_options);
    if (teamMemberMaxCapacity != null && selectedTeamMemberIds.length >= teamMemberMaxCapacity) {
      return;
    }
    const newMember: TeamMemberSchema = {
      ...member,
      id: `team-${Date.now()}`,
    };
    setAvailableTeamMembers((prev) => [...prev, newMember]);
    setSelectedTeamMemberIds((prev) => [...prev, newMember.id]);
  }, [availableTeamMembers, currentResource?.resource_options, selectedTeamMemberIds.length]);

  const resourceCapacityLimits = useMemo(
    () => getResourceCapacityLimits(currentResource?.resource_options),
    [currentResource?.resource_options]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'total_hours') {
      setNHours(parseFloat(value));
    }
    
    if (name){
      setForm((prev) => prev ? { ...prev, [name]: value } : prev);
    }
    // setForm((prev) => prev ? { ...prev, [name]: value } : prev);
  };

  const handleSave = () => {
    const bookingId = booking?.id || ""
    const communityId = booking?.community_id || ""

    if (onSave && bookingId && communityId && form) {
      const updatedForm = {
        ...form,
        total_cost: totalCost,
        plan_id: plan?.plan_id,
        plan_name: plan?.plan_name,
        plan_recurrence: plan?.plan_recurrence,
        plan_rate: plan?.plan_rate,
        team_members: form.team_members ?? [],
      };

      // if (form?.fees && form?.fees.length > 0) {
      //   updatedForm.fees = form?.fees
      // }

      if (paymentMethod?.id) {
        updatedForm.payment_method = paymentMethod?.id
      }

      const changedAttributes = getChangedAttributes(booking || {} as ResourceBookingSchema, updatedForm);
      const bookingTeamMembers = JSON.stringify(booking?.team_members ?? []);
      const updatedTeamMembers = JSON.stringify(updatedForm.team_members ?? []);
      if (bookingTeamMembers !== updatedTeamMembers) {
        changedAttributes.team_members = updatedForm.team_members ?? [];
      }
      // Make start_time and end_time timezone aware if they exist in changedAttributes
      if (changedAttributes.start_time || changedAttributes.end_time) {
        const timezone = booking?.timezone || 'America/New_York';
        
        if (changedAttributes.start_time) {
          changedAttributes.start_time = moment(changedAttributes.start_time).tz(timezone).format();
        }
        
        if (changedAttributes.end_time) {
          changedAttributes.end_time = moment(changedAttributes.end_time).tz(timezone).format();
        }
      }
      
      if (bookingId && communityId && Object.keys(changedAttributes).length > 0){
        
        onSave(communityId, bookingId, changedAttributes);
      }
    }
  };

  const handleCancelBooking = () => {
    const bookingId = booking?.id || ""
    const communityId = booking?.community_id || ""
    const resourceId = booking?.resource_id || ""
    if (bookingId && communityId && resourceId && onCancel) {
      onCancel(communityId, resourceId, bookingId);
    }
  };

  const handleDateChange = useCallback((date: Date) => {
    setSelectedDay(date);
    if (form?.start_time && form?.end_time) {
      let newStartTime: moment.Moment, newEndTime: moment.Moment;
      
      // If we have available time blocks, use the first one's time portions
      if (availableTimeBlocks && availableTimeBlocks.length > 0) {
        const firstBlock = availableTimeBlocks[0];
        newStartTime = moment(date).set({
          hour: moment(firstBlock.start).hour(),
          minute: moment(firstBlock.start).minute()
        });
        newEndTime = moment(date).set({
          hour: moment(firstBlock.end).hour(),
          minute: moment(firstBlock.end).minute()
        });
      } else {
        // Fall back to current form values if no available blocks
        const currentStartTime = moment(form.start_time);
        const currentEndTime = moment(form.end_time);
        
        newStartTime = moment(date).set({
          hour: currentStartTime.hour(),
          minute: currentStartTime.minute()
        });
        newEndTime = moment(date).set({
          hour: currentEndTime.hour(),
          minute: currentEndTime.minute()
        });
      }

      setForm(prev => ({
        ...prev,
        start_time: newStartTime.format('YYYY-MM-DDTHH:mm'),
        end_time: newEndTime.format('YYYY-MM-DDTHH:mm')
      }));
    }
    setShowCalendar(false);
  }, [form, availableTimeBlocks]);

  const handleTimeChange = (startTime: moment.Moment, endTime: moment.Moment) => {
    if (form) {
      const duration = moment.duration(endTime.diff(startTime));
      const hoursDiff = duration.asHours();
      setNHours(hoursDiff);
      
      // Ensure the times are set on the selected day
      const newStartTime = moment(selectedDay).set({
        hour: startTime.hour(),
        minute: startTime.minute()
      });
      const newEndTime = moment(selectedDay).set({
        hour: endTime.hour(),
        minute: endTime.minute()
      });

      setForm(prev => ({
        ...prev,
        start_time: newStartTime.format('YYYY-MM-DDTHH:mm'),
        end_time: newEndTime.format('YYYY-MM-DDTHH:mm'),
        total_hours: hoursDiff
      }));
    }
  };

  const handleTimeBlockSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedBlock = availableTimeBlocks?.find(block => 
      `${moment(block.start).format("hh:mm A")} - ${moment(block.end).format("hh:mm A")}` === e.target.value
    );
    
    if (selectedBlock) {
      handleTimeChange(moment(selectedBlock.start), moment(selectedBlock.end));
    }
  };

  const handlePlanChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedMethod = plans.find((p: ResourcePlanSchema) => p.plan_id === e.target.value);
    setPlan(selectedMethod || {} as ResourcePlanSchema);
    // setIsChangePlan(false);
  };

  const nextMonth = () => {
    setCurrentDate(addMonths(currentCalendarDate, 1));
  };

  const prevMonth = () => {
    const newDate = addMonths(currentCalendarDate, -1);
    // Only allow going back if the new date is not before today
    // if (newDate >= new Date()) {
      setCurrentDate(newDate);
    // }
  };

  // const userBookings = useMemo(() => [] as BookingSchema[], []);

  var { resourceBookings } = useLoadResourceBookings(booking?.community_id || "", booking?.resource_id || "");

  // const resourceBookings = useMemo(() => [] as ResourceBookingSchema[], []);
  // console.log("resourceBookings", resourceBookings)
  // console.log("userBookings", userBookings)


  useEffect(() => {
    const combinedBookings = [...(userBookings || []), ...(resourceBookings || [])];

    if (
      !areDatesInSameMonth(selectedDay, currentCalendarDate) ||
      !areBookingsLessThan12Hours(selectedDay, combinedBookings) ||
      nHours <= 0
    ) {
      return;
    }

    const dayOfWeek = moment(selectedDay).format("ddd").toLowerCase();
    const dayOptions = currentResource?.resource_options?.[dayOfWeek] as TimeSlot[];

    if (!dayOptions || !Array.isArray(dayOptions) || dayOptions.length === 0) {
      return;
    }

    const bufferMinutes = currentResource?.resource_options?.floor_plan?.buffer_time ?? 0;
    const availableBlocks = calculateAvailableTimeBlocksForSlots(
      dayOptions,
      nHours,
      findBookingsForDay(moment(selectedDay), combinedBookings),
      moment(selectedDay),
      bufferMinutes
    );

    // Only update if different to avoid unnecessary renders
    setAvailableTimeBlocks((prev) => {
      const prevStr = JSON.stringify(prev);
      const newStr = JSON.stringify(availableBlocks);
      return prevStr !== newStr ? availableBlocks : prev;
    });

    // Optionally update form times here if needed, but be careful to avoid loops!
  }, [
    selectedDay,
    nHours,
    currentCalendarDate,
    currentResource,
    userBookings,
    resourceBookings,
    form.start_time,
    form.end_time,
  ]);

  var bookingStatusColor: any = getBookingStatusColor(form?.status ? form.status : 'pending')
  const bookingStatusBgColor = bookingStatusColor.bgColor
  bookingStatusColor = bookingStatusColor.primaryTextColor

  const isFundingBooking = form.resource_type === 'funding';
  const isFinancialIntelligenceBooking = isFundingBooking && (
    Boolean(form.referral_id) || isFinancialIntelligenceResource(currentResource)
  );
  const showPricingSection = !isFinancialIntelligenceBooking;
  const showPaymentSection = !isFinancialIntelligenceBooking;

  useEffect(() => {
    if (booking?.id || booking?.resource_type) {
      if (booking.start_time) {
        setSelectedDay(new Date(booking.start_time));
      }
      setForm(booking);
      updateResourceId(booking.resource_id || "");
      setNHours(booking.total_hours || 0);
      setPlan(extractPlanFields(booking) as ResourcePlanSchema);
    }
  }, [booking, updateResourceId]);

  useEffect(() => {
    const memberPool = normalizeTeamMembers(currentMember?.team_members ?? []);
    const bookingMembers = normalizeTeamMembers(booking?.team_members ?? []);
    const merged = mergeTeamMemberPools(memberPool, bookingMembers);
    setAvailableTeamMembers(merged);

    const selectedIds = bookingMembers
      .map((member) => merged.find((poolMember) =>
        poolMember.email.trim().toLowerCase() === member.email.trim().toLowerCase()
      )?.id)
      .filter((id): id is string => Boolean(id));
    setSelectedTeamMemberIds(selectedIds);
  }, [booking?.id, booking?.team_members, currentMember?.team_members]);

  useEffect(() => {
    if (
      booking?.payment_method &&
      paymentMethods?.length > 0 &&
      !initialPaymentMethodSet.current
    ) {
      const matchingMethod = paymentMethods.find((p: PaymentMethod) => p.id === booking?.payment_method);
      if (matchingMethod) {
        setPaymentMethod(matchingMethod);
      } else if (paymentMethods.length > 0) {
        setPaymentMethod(paymentMethods[0]);
      }
      initialPaymentMethodSet.current = true;
    }
  }, [booking?.payment_method, paymentMethods, initialPaymentMethodSet]);


  // console.log("paymentMethod", paymentMethod)

  // const feesCost = form?.fees && form?.fees.length > 0 ? form?.fees.reduce((acc: number, fee: FeeSchema) => acc + fee.rate, 0) : 0;
  
  // var hourlyCost = (plan?.plan_rate && plan?.plan_recurrence  === 'hourly') ? plan.plan_rate * nHours : plan?.plan_rate;
  // hourlyCost = (hourlyCost && plan?.plan_recurrence  === 'hourly') ? hourlyCost : 0;
  
  // var hoursBalance = (plan?.plan_recurrence && member?.member_balance && currentKitchen?.capacity) ? maxHours(parseInt(currentKitchen.capacity), plan.plan_recurrence, member?.member_balance) - hoursDiff : 0;
  // const totalCost = hourlyCost + feesCost

  // console.log("currentMemberBookingPlanId", currentMemberBookingPlanId)
  // console.log("currentMemberBookingBalance", currentMemberBookingBalance)

  // const { currentMember } = useLoadMember(booking?.community_id || "");

  // const currentMemberBookingPlanIds = currentMember?.booking_plan_ids || ''
  // const currentMemberBookingBalances = currentMember?.booking_balances || ''
  // const currentMemberBookingPlanId = extractMemberResourceMapFromJsonString(currentMemberBookingPlanIds, form?.resource_id || '', '')
  // const currentMemberBookingBalance = extractMemberResourceMapFromJsonString(currentMemberBookingBalances, form?.resource_id || '', 0)


  // const isFirstPlanBooking = isFirstBookingWithPlan(currentMember?.username || '', currentMember, plan?.resource_id || '', plan?.plan_id || '');
  
  // Memoize subtotal cost calculation
  const subtotalCost = useMemo(() => {
    // const isFirstPlanBooking = isFirstBookingWithPlan(form?.username || '', userBookings || [] as ResourceBookingSchema[], plan?.plan_id || '', plan?.plan_id || '');
    // const baseCost = (plan?.plan_rate && plan?.plan_recurrence === 'hourly') 
    //   ? plan.plan_rate * nHours 
    //   : (plan?.plan_rate ?? 0);


      
      const baseCost = (plan?.plan_rate && plan?.plan_recurrence === 'hourly') 
        ? (plan.plan_rate * nHours) 
        : (form?.subtotal_cost ? form?.subtotal_cost :  0);
    // const cost = isFirstPlanBooking ? baseCost : 0;
    
    // console.log('Subtotal Cost Recalculated:', {
    //   hoursDiff,
    //   planRate: plan?.plan_rate,
    //   planRecurrence: plan?.plan_recurrence,
    //   isFirstPlanBooking,
    //   baseCost,
    //   cost
    // });
    
    return baseCost;
  }, [plan, nHours, form?.subtotal_cost]);
  
  // const hoursBalance = useMemo(() => 
  //   (plan?.plan_recurrence && member?.member_balance && currentKitchen?.capacity) 
  //     ? maxHours(parseInt(currentKitchen.capacity), plan.plan_recurrence, member?.member_balance) - hoursDiff 
  //     : 0,
  //   [plan?.plan_recurrence, member?.member_balance, currentKitchen?.capacity, hoursDiff]
  // );

  const feesCost = useMemo(() => 
    form?.fees && form?.fees.length > 0 
        ? form?.fees?.reduce((acc: number, fee: FeeSchema) => acc + fee.rate, 0) 
      : 0,
    [form?.fees]
  );

  const orderInventoryItems = useMemo(
    () => form?.order_items ?? form?.order_inventory ?? [],
    [form?.order_items, form?.order_inventory]
  );

  const teamMembers = useMemo(
    () => form?.team_members ?? booking?.team_members ?? [],
    [form?.team_members, booking?.team_members]
  );

  /** Prefer the resource catalog plan so dual `plan_type` / associated inventory are available. */
  const pricingPlan = useMemo(() => {
    if (!plan?.plan_id) {
      return plan;
    }
    const availablePlans = currentResource?.plans ?? EMPTY_RESOURCE_PLANS;
    const matched = availablePlans.find((availablePlan) => availablePlan.plan_id === plan.plan_id);
    return matched ? { ...plan, ...matched } : plan;
  }, [plan, currentResource?.plans]);

  const applyInventoryLimits = shouldApplyMemberInventoryLimits(pricingPlan, currentMember);
  const inventoryLimitsForPricing = useMemo(
    () =>
      getInventoryLimitsForBookingPricing(
        currentMember,
        pricingPlan,
        form?.status ? form : booking
      ),
    [booking, currentMember, form, pricingPlan]
  );

  const inventoryCostBreakdown = useMemo(
    () =>
      calculateInventoryCostLinesWithLimits(
        orderInventoryItems.map((item) => ({
          inventoryId: item.inventory_id || item.id || '',
          totalQuantity: item.total_quantity,
          unitPrice: item.unit_price,
        })),
        inventoryLimitsForPricing,
        applyInventoryLimits
      ),
    [applyInventoryLimits, inventoryLimitsForPricing, orderInventoryItems]
  );

  const inventoryCost = inventoryCostBreakdown.inventoryCost;

  const checkDiscountActive = useCallback((discount: DiscountSchema): boolean => {
    if (!discount?.rate) return false;
    if (!form?.start_time || !form?.end_time) return false;
    
    const discountStartDate = discount.start_date ? moment(discount.start_date).startOf('day') : moment().startOf('day');
    const discountEndDate = discount.end_date ? moment(discount.end_date).endOf('day') : moment().endOf('day');
    const bookingStart = moment(form?.start_time).startOf('day');
    const bookingEnd = moment(form?.end_time).endOf('day');
    
    // Check if the booking period falls entirely within the discount period
    const isValid = bookingStart.isSameOrAfter(discountStartDate) && 
                    bookingStart.isSameOrBefore(discountEndDate) &&
                    bookingEnd.isSameOrAfter(discountStartDate) &&
                    bookingEnd.isSameOrBefore(discountEndDate);
    
    
    return isValid;
  }, [form?.start_time, form?.end_time]);
  
  // const cleaningCost = 5
  // const serviceCost = 3
  // const equipmentCost = 0
  
  const discountAmount = useMemo(() => {
    const amount = form?.discounts && form?.discounts.length > 0 
      ? form?.discounts?.reduce((acc: number, discount: DiscountSchema) => {
          if (checkDiscountActive(discount)) {
            if (discount.discount_type === 'fixed') {
              return acc + discount.rate;
            } else if (discount.discount_type === 'percentage') {
              return acc + (discount.rate * (subtotalCost || 0) / 100);
            }
          }
          return acc;
        }, 0)
      : 0;
    
    // console.log('Discount Amount Recalculated:', {
    //   amount,
    //   subtotalCost,
    //   startDate,
    //   endDate,
    //   hoursDiff
    // });
    
    return amount;
  }, [form?.discounts, checkDiscountActive, subtotalCost]);

  
  // helper to check if discount is active, i.e. discount start and end date is between booking start and end date
  // const isDiscountActive = currentKitchen.discounts && currentKitchen.discounts.length > 0 ? currentKitchen.discounts.some((discount: DiscountSchema) => {
  //   return checkDiscountActive(discount)
  // }) : false;

  // console.log("form.subtotal_cost", form?.subtotal_cost)
  // console.log("subtotalCost", subtotalCost)
  // console.log("discountAmount", discountAmount)
  // console.log("feesCost", feesCost)

  


  const totalCost = useMemo(() => {

    // const effectiveSubtotal = 
    // (currentMemberBookingBalance && currentMemberBookingPlanId && (currentMemberBookingPlanId === plan?.plan_id || !['hourly', 'daily'].includes(plan?.plan_recurrence || ''))) 
    //   ? 0 
    //   : 
    // !isFirstBookingWithPlan(currentMember?.username || '', currentMember, form?.resource_id || '', plan?.plan_id || '') ? 0 : subtotalCost;
      // subtotalCost;
      // console.log("effectiveSubtotal", effectiveSubtotal)
      // console.log("subtotalCost", subtotalCost)
      // console.log("form.subtotal_cost", form?.subtotal_cost)
      // console.log("feesCost", feesCost)
      // console.log("discountAmount", discountAmount)
      // const isFirstPlanBooking = isFirstBookingWithPlan(currentMember?.username || '', currentMember, plan?.resource_id || '', plan?.plan_id || '');
      // const effectiveSubtotal = (plan?.plan_rate && plan?.plan_recurrence === 'hourly') 
      //   ? plan.plan_rate * nHours 
      //   : isFirstPlanBooking ? (plan?.plan_rate ?? 0) : subtotalCost;
    const baseTotal = (subtotalCost || 0) + feesCost + inventoryCost;
    const finalTotal = (plan?.plan_recurrence === 'hourly')  ? (baseTotal - discountAmount) : (form?.total_cost ? form?.total_cost : (baseTotal - discountAmount));
    // const finalTotal = baseTotal - discountAmount;
    return Math.max(0, finalTotal); // Ensure total is never negative
  }, [subtotalCost, discountAmount, feesCost, inventoryCost, form?.total_cost, plan?.plan_recurrence]);
  
  return (
    <div className='flex flex-col justify-start h-full px-4'>
      <span onClick={onClose} className="mb-4 w-full flex cursor-pointer">
        <CancelIcon className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
      </span>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="font-semibold font-raleway text-lg">
            {isEditMode ? 'Edit booking details' : 'View booking details'}
          </h2>
        </div>
    
        {/* Status Badge */}
        {form.status && (
          <div
            className="font-raleway text-[15px] font px-3 py-1 border rounded-md text-xs font-semibold "
            style={{
              borderColor: bookingStatusColor,
              color: bookingStatusColor,
              backgroundColor: bookingStatusBgColor,
              zIndex: 10,
            }}
          >
            {capitalizeWords(form.status.replace(/_/g, ' '))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-[#808080] text-[16px] font-raleway font-medium">{capitalizeWords(form.resource_type || '')} Name</label>
          <div className="w-full text-[16px]">{form.resource_name || ''}</div>
        </div>

        {form.resource_type === 'documentation' && (
          <ApplicantDocumentationChecklist
            booking={form}
            steps={currentResource?.steps || []}
            onChecklistChange={(checklist) =>
              setForm((prev) => (prev ? { ...prev, applicant_checklist: checklist } : prev))
            }
          />
        )}

        {isFinancialIntelligenceBooking && (
          <>
            {form.business_name && (
              <div>
                <label className="block text-[#808080] text-[16px] font-raleway font-medium">Business name</label>
                <div className="w-full text-[16px]">{form.business_name}</div>
              </div>
            )}
            {form.business_type && (
              <div>
                <label className="block text-[#808080] text-[16px] font-raleway font-medium">Business type</label>
                <div className="w-full text-[16px]">{form.business_type}</div>
              </div>
            )}
            {form.location && (
              <div>
                <label className="block text-[#808080] text-[16px] font-raleway font-medium">Location</label>
                <div className="w-full text-[16px]">{form.location}</div>
              </div>
            )}
          </>
        )}

        {isEditMode ? (
          <>
            {(form.resource_type === 'kitchen') && (
            <>
              <div>
                <label className="block text-[#808080] text-[16px] font-raleway font-medium">Date</label>
                <div className="relative w-full mx-auto">
                  <button
                    type="button"
                    className="flex items-center w-full px-3 py-2 border-[1px] border-[#E9E9E9] rounded-lg bg-white focus:ring-1"
                    onClick={() => setShowCalendar(prev => !prev)}
                  >
                    <span className="flex-1 text-left text-[16px] text-[#4D4D4D] font-raleway font-medium">
                      {selectedDay ? moment(selectedDay).format('MM/DD/YYYY') : 'Select date'}
                    </span>
                    <span className="ml-2 p-1 rounded-full bg-[#F4F8FB] text-primaryDark-200">
                      <CalendarIcon />
                    </span>
                  </button>
                  {showCalendar && (
                    <div className="absolute z-50 mt-2 p-4 w-full bg-white border border-[#E9E9E9] rounded-lg shadow-lg">
                      <div className="flex flex-row mb-4">
                        <h2 className="text-[16px] text-[#4D4D4D] font-raleway font-medium text-center my-auto">
                          {format(currentCalendarDate, "MMMM yyyy")}
                        </h2>

                        <div className="flex-row justify-between items-center text-slate-400 my-auto ml-auto">
                          <button 
                            onClick={prevMonth}
                            className="mr-4 rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer m-auto"
                          >
                            <LeftArrow className=" text-black rounded-full  m-auto" />
                          </button>
                          <button 
                            onClick={nextMonth}
                            className="mr-4 rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer m-auto"
                          >
                              <RightArrow className="text-black rounded-full m-auto" />
                          </button>
                        </div>
                      </div>
                      <ReservationCalendar
                        currentCalendarDate={currentCalendarDate}
                        userBookings={userBookings || [] as ResourceBookingSchema[]}
                        resourceBookings={resourceBookings || [] as ResourceBookingSchema[]}
                        setBlocks={setAvailableTimeBlocks}
                        nHours={nHours}
                        selectedDay={selectedDay}
                        handleDateChange={handleDateChange}
                        currentKitchen={currentResource ? currentResource : {} as ResourceSchema}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-[#808080] text-[16px] font-raleway font-medium">Total Hours</label>
                <input
                  className="w-full border-[1px] border-[#E9E9E9] bg-white rounded p-3"
                  name="total_hours"
                  value={nHours.toFixed(1)}
                  onChange={handleChange}
                  type="number"
                  min={0.5}
                  max={24}
                  step={0.5}
                />  
              </div>

              <div>
                <label className="block text-[#808080] text-[16px] font-raleway font-medium">
                  Available time <span className='text-sm my-auto font-bold'>({moment(selectedDay).format("ddd, LL")})</span>
                </label>
                <div className="w-full">
                  {/* <p className="text-black font-medium text-left text-[16px] font-raleway mt-3">
                    Select an available time for <b>{moment(selectedDay).format("ddd, LL")}</b>
                  </p> */}
                  <div className="w-full">
                    <select
                      className="w-full border-[1px] border-[#E9E9E9] bg-white rounded p-3 text-[16px] font-open-sans"
                      onChange={handleTimeBlockSelect}
                      value={form?.start_time && form?.end_time ? 
                        `${moment(form.start_time).format("hh:mm A")} - ${moment(form.end_time).format("hh:mm A")}` : 
                        ''}
                    >
                      <option disabled value="">-- Select Time Block --</option>
                      {availableTimeBlocks?.map((block, index) => (
                        <option key={index} value={`${moment(block.start).format("hh:mm A")} - ${moment(block.end).format("hh:mm A")}`}>
                          {moment(block.start).format("hh:mm A")} - {moment(block.end).format("hh:mm A")}
                        </option>
                      ))}
                      {
                        !availableTimeBlocks?.length && form?.start_time && form?.end_time && (
                          <option
                            value={`${moment(form.start_time).format("hh:mm A")} - ${moment(form.end_time).format("hh:mm A")}`}
                          >
                            {moment(form.start_time).format("hh:mm A")} - {moment(form.end_time).format("hh:mm A")}
                          </option>
                        )
                      }
                    </select>

                  </div>
                </div>
                {/* <TimesView
                  selectedDay={selectedDay}
                  updateEndTime={(endTime: moment.Moment) => handleTimeChange(moment(form?.start_time || ''), endTime)}
                  updateStartTime={(startTime: moment.Moment) => handleTimeChange(startTime, moment(form?.end_time || ''))}
                  availableTimeBlocks={availableTimeBlocks}
                  selectedBlock={selectedBlock}
                  setSelectedBlock={setSelectedBlock}
                /> */}
              </div>

              <div>
                  <label className="block text-[#808080] text-[16px] font-raleway font-medium">Plan</label>
                  <div className="flex flex-col w-full h-full text-left mx-auto">
                    <select 
                      value={plan?.plan_id} 
                      onChange={handlePlanChange} 
                      className="border-[1px] border-[#E9E9E9] rounded-lg text-[16px] text-[#808080] font-open-sans"
                    >
                      <option value="">Choose a plan</option>
                      {plans.map((p: PlanSchema) => (
                        <option key={p.plan_id} value={p.plan_id}>
                          {p.plan_name} - ${styleThousandCommas(p.plan_rate?.toFixed(2) || '0.00')} {p.plan_recurrence}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
              )
            }

            {isEditMode ? (
              <AddTeamMembersView
                availableTeamMembers={availableTeamMembers}
                selectedTeamMemberIds={selectedTeamMemberIds}
                setSelectedTeamMemberIds={setBookingTeamMemberIds}
                onInviteMember={handleInviteTeamMember}
                embedded
                maxCapacity={resourceCapacityLimits.teamMemberMaxCapacity}
                totalCapacity={resourceCapacityLimits.totalCapacity}
              />
            ) : (
              teamMembers.length > 0 && (
                <div>
                  <label className="block text-[#808080] text-[16px] font-raleway font-medium">Team Members</label>
                  <div className="w-full py-2 space-y-2">
                    {teamMembers.map((member, index) => (
                      <div key={member.id || member.email || `team-member-${index}`} className="flex items-center gap-2">
                        <UserAvatar
                          firstName={member.first_name}
                          lastName={member.last_name}
                          email={member.email}
                          size="sm"
                        />
                        <span className="text-[16px]">{getTeamMemberDisplayName(member)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}

            <div>
              <label className="block text-[#808080] text-[16px] font-raleway font-medium">Additional Notes</label>
              <textarea
                className="w-full border-[1px] border-[#E9E9E9] bg-white rounded-lg py-2"
                name="notes"
                value={form.notes || ''}
                onChange={handleChange}
              />
            </div>

            {showPricingSection && (
              <>
            <div className="flex flex-row justify-between w-full font-bold text-lg mb-1">
              <span className="block text-[16px] text-[#808080] my-auto font-raleway font-medium">Subtotal</span>
              <span className="ml-auto my-auto text-[16px] text-black">${subtotalCost ? styleThousandCommas(subtotalCost.toFixed(2)) : '0.00'}</span>
            </div> 

            {
              form?.fees && form?.fees.length > 0 && (
                form?.fees.map((fee: FeeSchema, index: number) => (
                  <div key={`fee-${fee.id || index}`} className="flex flex-row justify-between w-full font-bold text-lg mb-1">
                    <span className="block text-[15px] text-[#808080]/70 my-auto font-raleway font-medium">{fee.name}</span>
                    <span className="ml-auto my-auto text-[16px] text-black/70">${fee.rate ? styleThousandCommas(fee.rate.toFixed(2)) : '0.00'}</span>
                  </div>  
                ))
              )
            }

            {
              form?.discounts && form?.discounts.length > 0 && (
                form?.discounts
                  .filter((discount: DiscountSchema) => checkDiscountActive(discount))
                  .map((discount: DiscountSchema, index: number) => (
                    <div key={`discount-${discount.id || index}`} className="flex flex-row justify-between w-full font-bold text-lg mb-1">
                      <span className="block text-[15px] text-[#808080]/70 my-auto font-raleway font-medium">{discount.name} {discount.discount_type === 'percentage' ? `(${discount.rate}% off)` : ''}</span>
                      <span className="ml-auto my-auto text-[16px] text-black/70">
                      {/* ${discount.rate ? styleThousandCommas(discount.rate.toFixed(2)) : '0.00'
                      } */}
                      {(discount.discount_type === 'percentage') ? `($${(discount.rate * (subtotalCost|| 0) / 100).toFixed(2)})` : (discount.rate && discount.discount_type === 'fixed') ? `($${discount.rate.toFixed(2)})` : 0}
                      </span>
                    </div>  
                  ))
              )
            }

            {
              orderInventoryItems.length > 0 && orderInventoryItems.map((item: OrderItemSchema, index: number) => {
                const line = inventoryCostBreakdown.lines[index];
                const fullPrice = (item.unit_price ?? 0) * item.total_quantity;
                const includedQty = line?.includedQty ?? 0;
                const billableQty = line?.billableQty ?? item.total_quantity;
                const lineCost = line?.lineCost ?? fullPrice;
                const remainingAfterThis = line?.remainingAfterThis ?? 0;
                const isFullyIncluded = includedQty > 0 && billableQty === 0;
                const isPartialIncluded = includedQty > 0 && billableQty > 0;

                return (
                <div key={`inventory-edit-${item.inventory_id || item.id || index}`} className="flex flex-row justify-between w-full font-bold text-lg mb-1">
                  <div className="flex flex-col min-w-0 my-auto">
                    <span className="block text-[15px] text-[#808080]/70 font-raleway font-medium">
                      {item.item_name} x{item.total_quantity}
                    </span>
                    {isFullyIncluded && (
                      <span className="block text-[12px] text-primaryDark-300/70 font-raleway font-normal">
                        {remainingAfterThis > 0
                          ? `${remainingAfterThis} included left`
                          : `${includedQty} included`}
                      </span>
                    )}
                    {isPartialIncluded && (
                      <span className="block text-[12px] text-primaryDark-300/70 font-raleway font-normal">
                        {includedQty} included / {billableQty} charged
                      </span>
                    )}
                  </div>
                  <span className="ml-auto my-auto text-[16px] text-black/70">
                    {isFullyIncluded ? (
                      <span className="flex flex-col items-end">
                        <span className="line-through text-gray-400 text-[13px] font-normal">
                          ${styleThousandCommas(fullPrice.toFixed(2))}
                        </span>
                        <span>$0.00</span>
                      </span>
                    ) : (
                      `$${styleThousandCommas(lineCost.toFixed(2))}`
                    )}
                  </span>
                </div>
                );
              })
            }

            <div className="flex items-center flex-col justify-between mt-4">
              <div className="flex flex-row justify-between w-full font-bold text-lg mb-1">
                <span className="block text-[16px] text-[#808080] my-auto font-raleway font-medium">Total</span> 
                {
                  (form?.total_cost?.toFixed(2) !== totalCost?.toFixed(2)) && (<span className="ml-auto my-auto line-through text-gray-400 text-[16px]">${form.total_cost ? styleThousandCommas(form.total_cost.toFixed(2)) : '0.00'}</span>)
                }
                <span className="ml-4 my-auto">${totalCost ? styleThousandCommas(totalCost.toFixed(2)) : '0.00'}</span>
              </div>
            </div>
              </>
            )}

            {showPaymentSection && (
            <div className='flex flex-col justify-between w-full text-lg mb-1'>
              <label className="block text-[#808080] text-[16px] font-raleway font-medium">Payment Method</label>
              {(!isEditPay && (paymentMethod && paymentMethod?.card)) ? (
              <div className="flex flex-col justify-between">
                {
                  <p className="font-semibold text-[16px] text-primaryDark-200">
                    {`${paymentMethod?.card?.brand?.toUpperCase()} **** ${paymentMethod?.card?.last4} ${paymentMethod?.card?.exp_month?.toString().padStart(2, '0')}/${paymentMethod?.card?.exp_year?.toString().slice(-2)}`}
                  </p>
                }
                <h1 className='cursor-pointer text-primaryDark-400 ml-auto text-sm' onClick={() => setIsEditPay(true)}>Edit</h1>
              </div>
              ) : (
                <div className="flex flex-col w-full">
                  <PaymentsView 
                    isAddingPayMethod={isAddingPayMethod}
                    setIsAddingPayMethod={setIsAddingPayMethod}
                    paymentMethods={paymentMethods || [] as PaymentMethod[]}
                    paymentMethod={paymentMethod || {} as PaymentMethod} 
                    setPaymentMethod={setPaymentMethod}
                    setIsEditPay={setIsEditPay} 
                  />
                </div>
              )}
            </div>
            )}
          </>
        ) : (
          <>

            <div>
              <label className="block text-[#808080] mb-1 text-[16px] font-raleway font-medium">{isFundingBooking ? 'Request date' : 'Date'}</label>
              <div className="w-full py-2 text-[16px]">
                {form.start_time ? moment(form.start_time).format('ddd, MMMM D, YYYY') : ''}
              </div>
            </div>

            {(form.resource_type === 'kitchen') && (
                <div>
                  <label className="block text-[#808080] mb-1 text-[16px] font-raleway font-medium">Total Hours</label>
                  <div className="w-full py-2 text-[16px]">{nHours}</div>
                </div>
              )
            }

            {(form.resource_type === 'kitchen') && (
            <>
              <div>
                  <label className="block text-[#808080] mb-1 text-[16px] font-raleway font-medium">Time</label>
                  <div className="w-full py-2 text-[16px]">
                  {form.start_time && form.end_time ? `${moment(form.start_time).format('hh:mm A')} - ${moment(form.end_time).format('hh:mm A')}` : ''}
                </div>
              </div>
              <div>
                  <label className="block text-[#808080] mb-1 text-[16px] font-raleway font-medium">Plan</label>
               
                  <div className="w-full py-2 text-[16px]">{form.plan_name} - ${styleThousandCommas(form.plan_rate?.toFixed(2) || '0.00')} {form.plan_recurrence}</div>
                 
              </div>
            </>
            )}


            {teamMembers.length > 0 && (
              <div>
                <label className="block text-[#808080] text-[16px] font-raleway font-medium">Team Members</label>
                <div className="w-full py-2 space-y-2">
                  {teamMembers.map((member, index) => (
                    <div key={member.id || member.email || `team-member-${index}`} className="flex items-center gap-2">
                      <UserAvatar
                        firstName={member.first_name}
                        lastName={member.last_name}
                        email={member.email}
                        size="sm"
                      />
                      <span className="text-[16px]">{getTeamMemberDisplayName(member)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-[#808080] text-[16px] font-raleway font-medium">Additional Notes</label>
              <div className="w-full py-2 text-[16px] whitespace-pre-wrap">{form.notes || 'N/A'}</div>
            </div>

            {showPricingSection && (
              <>
            <div className="flex flex-row justify-between w-full font-bold text-lg mb-1">
              <span className="block text-[16px] text-[#808080] my-auto font-raleway font-medium">Subtotal</span>
              <span className="ml-auto my-auto text-[16px] text-black">${form?.subtotal_cost ? styleThousandCommas(form?.subtotal_cost.toFixed(2)) : '0.00'}</span>
            </div>  


            {
              form?.fees && form?.fees.length > 0 && (
                form?.fees.map((fee: FeeSchema, index: number) => (
                  <div key={`fee-view-${fee.id || index}`} className="flex flex-row justify-between w-full font-bold text-lg mb-1">
                    <span className="block text-[15px] text-[#808080]/70 my-auto font-raleway font-medium">{fee.name}</span>
                    <span className="ml-auto my-auto text-[16px] text-black/70">${fee.rate ? styleThousandCommas(fee.rate.toFixed(2)) : '0.00'}</span>
                  </div>  
                ))
              )
            }

            {
              form?.discounts && form?.discounts.length > 0 && (
                form?.discounts
                  .filter((discount: DiscountSchema) => checkDiscountActive(discount))
                  .map((discount: DiscountSchema, index: number) => (
                    <div key={`discount-view-${discount.id || index}`} className="flex flex-row justify-between w-full font-bold text-lg mb-1">
                      <span className="block text-[15px] text-[#808080]/70 my-auto font-raleway font-medium">{discount.name} {discount.discount_type === 'percentage' ? `(${discount.rate}% off)` : ''}</span>
                      <span className="ml-auto my-auto text-[16px] text-black/70">
                      {/* ${discount.rate ? styleThousandCommas(discount.rate.toFixed(2)) : '0.00'
                      } */}
                      {(discount.rate && discount.discount_type === 'percentage') ? `($${(discount.rate * (form.subtotal_cost || 0) / 100).toFixed(2)})` : (discount.rate && discount.discount_type === 'fixed') ? `($${discount.rate.toFixed(2)})` : 0}
                      </span>
                    </div>  
                  ))
              )
            }

            {
              orderInventoryItems.length > 0 && orderInventoryItems.map((item: OrderItemSchema, index: number) => {
                const line = inventoryCostBreakdown.lines[index];
                const fullPrice = (item.unit_price ?? 0) * item.total_quantity;
                const includedQty = line?.includedQty ?? 0;
                const billableQty = line?.billableQty ?? item.total_quantity;
                const lineCost = line?.lineCost ?? fullPrice;
                const remainingAfterThis = line?.remainingAfterThis ?? 0;
                const isFullyIncluded = includedQty > 0 && billableQty === 0;
                const isPartialIncluded = includedQty > 0 && billableQty > 0;

                return (
                <div key={`inventory-view-${item.inventory_id || item.id || index}`} className="flex flex-row justify-between w-full font-bold text-lg mb-1">
                  <div className="flex flex-col min-w-0 my-auto">
                    <span className="block text-[15px] text-[#808080]/70 font-raleway font-medium">
                      {item.item_name} x{item.total_quantity}
                    </span>
                    {isFullyIncluded && (
                      <span className="block text-[12px] text-primaryDark-300/70 font-raleway font-normal">
                        {remainingAfterThis > 0
                          ? `${remainingAfterThis} included left`
                          : `${includedQty} included`}
                      </span>
                    )}
                    {isPartialIncluded && (
                      <span className="block text-[12px] text-primaryDark-300/70 font-raleway font-normal">
                        {includedQty} included / {billableQty} charged
                      </span>
                    )}
                  </div>
                  <span className="ml-auto my-auto text-[16px] text-black/70">
                    {isFullyIncluded ? (
                      <span className="flex flex-col items-end">
                        <span className="line-through text-gray-400 text-[13px] font-normal">
                          ${styleThousandCommas(fullPrice.toFixed(2))}
                        </span>
                        <span>$0.00</span>
                      </span>
                    ) : (
                      `$${styleThousandCommas(lineCost.toFixed(2))}`
                    )}
                  </span>
                </div>
                );
              })
            }

            <div className="flex flex-row justify-between w-full font-bold text-lg mb-1">
              <span className="block text-[16px] text-[#808080] my-auto font-raleway font-medium">Total</span> 
              {/* <span className="ml-auto my-auto">${form.total_cost ? styleThousandCommas(form.total_cost.toFixed(2)) : '0.00'}</span> */}
              <span className="ml-auto my-auto">${totalCost ? styleThousandCommas(totalCost.toFixed(2)) : '0.00'}</span>
            </div>
              </>
            )}

            {showPaymentSection && (
            <div className='flex flex-row justify-between w-full font-bold text-lg mb-1'>
              <label className="block text-[#808080] mb-1 text-[16px] font-raleway font-medium">Payment Method</label>
              <p className="font-semibold text-primaryDark-300 text-[16px]">
                {`${paymentMethod?.card?.brand?.toUpperCase()} **** ${paymentMethod?.card?.last4} ${paymentMethod?.card?.exp_month?.toString().padStart(2, '0')}/${paymentMethod?.card?.exp_year?.toString().slice(-2)}`}
              </p>
            </div>
            )}
          </>
        )}
      </div>


      <div className="mt-6 flex flex-col gap-2">
        <button
          className="w-full py-3 bg-[#00363A] text-white font-semibold rounded-lg text-[17px] transition-colors duration-200 hover:bg-[#00555A]"
          onClick={() => {
            const communityId = booking?.community_id || form?.community_id;
            if (!loggedinUid) {
              searchFilterOptions?.setSignInModalIsOpen(true);
              return;
            }
            if (communityId) {
              searchFilterOptions?.setMessageCommunityId(communityId);
            }
            searchFilterOptions?.setMessagesOpen(true);
          }}
        >
          Send a Message
        </button>
        {isEditMode && (
          <button
            className="w-full py-3 border-[1px] border-[#E9E9E9] bg-white text-primaryDark-0 font-semibold rounded-lg text-[17px] transition-colors duration-200 hover:bg-primaryDark-300 hover:text-white"
            onClick={handleSave}
          >
            Save Changes
          </button>
        )}
        {
          !(form.status === 'canceled' || form.status === 'completed') && (
            <button className="w-full border-0 bg-white py-3 text-red-500 font-medium rounded-lg text-[17px] transition-colors duration-200 hover:bg-red-100" onClick={handleCancelBooking}>
              {isFundingBooking ? 'Cancel Request' : 'Cancel Reservation'}
            </button>
          )
        }
      </div>
    </div>
  );
};

export default BookingDetailsForm; 